import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  Typography,
} from "@material-ui/core"
import { useContext, useEffect, useState } from "react"
import { useQueryClient } from "react-query"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "~/components/Common/Box"
import {
  DEFAULT_PERMISSIONS,
  PERMISSIONS_OPTIONS,
} from "../../constants/permissions"
import useStyles from "./styles"

const PermissionsDialog = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()

  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)
  const thisDialog = dialogs?.["permissionsDialog"] || {}
  const currentAdminName = thisDialog?.data?.admin?.name
  const currentAdminId = thisDialog?.data?.admin?.id
  const currentAdminPermissionGroup = thisDialog?.data?.admin?.permission_group
  const currentAdminPermissions = thisDialog?.data?.admin?.permissions
  const setPermissionsMutation = thisDialog?.data?.setPermissionsMutation
  const { open = false, data } = thisDialog
  const openErrorSnackbar = useOpenErrorSnackbar()
  const [loading, setLoading] = useState(false)
  const [permissionGroup, setPermissionGroup] = useState("read-only")
  const [permissions, setPermissions] = useState([])

  const handleClose = () => {
    closeDialog("permissionsDialog")
  }

  useEffect(() => {
    if (open) {
      if (currentAdminPermissionGroup === "super") {
        setPermissions(DEFAULT_PERMISSIONS)
      } else {
        setPermissions(
          currentAdminPermissions?.split(",")?.map((p) => p?.trim()) || [],
        )
      }
      setPermissionGroup(currentAdminPermissionGroup || "read-only")
    }
  }, [open])

  const handleChangePermissions = (permission) => {
    if (permissions.includes(permission)) {
      setPermissions((oldPermissions) =>
        oldPermissions.filter((x) => x !== permission),
      )
    } else {
      setPermissions((oldPermissions) => [...oldPermissions, permission])
    }
  }

  const handleChangePermissionGroup = (permissionGroup) => {
    setPermissionGroup(permissionGroup)
    if (permissionGroup === "super") {
      setPermissions(DEFAULT_PERMISSIONS)
    } else {
      setPermissions([])
    }
  }

  const handleSubmit = () => {
    setLoading(true)
    const data = {
      id: currentAdminId,
      permission_group: permissionGroup,
      permissions:
        permissions?.length > 0 ? permissions?.join(", ") : undefined,
    }

    setPermissionsMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("admin", res.data).then((serializedData) => {
          queryClient.setQueryData("admins", (oldData) => {
            let newArray = oldData.filter((x) => x.id !== res.data.id)
            return [...newArray, serializedData]
          })
        })
        openSnackBar({
          message: "Admin updated",
        })
        handleClose()
      },
      onError: (err) => {
        openErrorSnackbar(err, "There was an error updating admin")
      },
      onSettled: () => setLoading(false),
    })
  }
  return (
    <Dialog open={open} maxWidth="xs">
      <DialogTitle disabledTypography>
        <Typography variant="h3" color="primary" style={{ fontWeight: 600 }}>
          Admin Permissions {currentAdminName && `(${currentAdminName})`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box marginBottom="16px">
          <Typography color="secondary" className={classes.title}>
            Permission Group
          </Typography>

          <FormControl
            variant="outlined"
            style={{ minWidth: 200, backgroundColor: "#fff" }}
          >
            <Select
              value={permissionGroup}
              onChange={(e) => handleChangePermissionGroup(e?.target?.value)}
            >
              <MenuItem value={"read-only"}>Read Only</MenuItem>
              <MenuItem value={"admin"}>Admin</MenuItem>
              <MenuItem value={"super"}>Super Admin</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider />
        <Box marginTop="16px">
          <Typography color="secondary" className={classes.title}>
            Permissions
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {PERMISSIONS_OPTIONS?.map((permission, i) => (
              <FormControlLabel
                key={i}
                style={{ width: "45%" }}
                label={permission?.name}
                disabled={
                  permissionGroup === "super" || permissionGroup === "read-only"
                }
                control={
                  <Radio
                    onClick={(e) => handleChangePermissions(e?.target?.value)}
                    value={permission?.value}
                    checked={permissions.includes(permission?.value)}
                  />
                }
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoaderButton
          onClick={handleSubmit}
          classoverrides={{
            wrapper: classes.button,
            button: {
              root: classes.buttonRoot,
            },
          }}
          working={loading}
          variant="contained"
          color="secondary"
          size="small"
        >
          Save
        </LoaderButton>
      </DialogActions>
    </Dialog>
  )
}

export default PermissionsDialog
